import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { RequestsApiService } from './requests-api.service';
import {
  BehaviorSubject,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import {
  ColumnFiltersTranslations,
  ColumnFiltersTranslationsPayload,
  Counterparty,
  InstallerPerson,
  InstallersGroup,
  RequestQualityStatusesTranslationsPayload,
  RequestStatus,
  RequestStatusesIdsTranslationsPayload,
} from '@core/types';
import { ActivatedRoute } from '@angular/router';
import { isAnonymousAuthRejection, toFiniteNumbers } from '@core/helpers';
import {
  ActiveFilterChip,
  FilterQuery,
} from '@features/requests/request-list/types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  HIDE_COMPLETED_FILTER,
  MOBILE_REQUESTS_LIST_COMPACT_MODE,
  REQUEST_QUALITY_STATUSES_TRANSLATION_KEY,
  REQUEST_STATUSES_TRANSLATION_KEY,
  SHOW_IN_WORK_FILTER,
  SHOW_TODAY_FILTER,
  SHOW_TOMORROW_FILTER,
} from '@core/constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import {
  LOGISTIC_REQUEST_TYPE_LABELS,
  LogisticRequestType,
  REQUEST_STATUS_ID_TO_NAME,
} from '@outkeep/shared';
import type {
  InstallersLoadResult,
  LoadedRequestFiltersData,
  RequestFiltersData,
} from './request-filters.types';

const TECHNICAL_FILTER_KEYS: Array<keyof FilterQuery> = ['assignedToMe'];

@Injectable({
  providedIn: 'root',
})
export class RequestFiltersService {
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly route = inject(ActivatedRoute);
  private filtersData = new BehaviorSubject<RequestFiltersData>({
    counterparties: null,
    installers: null,
    installersLoadFailed: false,
    statuses: null,
    installersGroups: null,
    requestTypes: null,
    products: null,
  });
  private readonly destroyRef = inject(DestroyRef);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly localStorageService = inject(LocalStorageService);

  private translations: RequestQualityStatusesTranslationsPayload &
    RequestStatusesIdsTranslationsPayload &
    Partial<ColumnFiltersTranslationsPayload> = {
    [REQUEST_QUALITY_STATUSES_TRANSLATION_KEY]: {
      '0': 'Не проверена',
      '1': 'Заявки без ошибок',
      '2': 'Заявки с ошибками',
      '3': 'Заявки с критическими ошибками',
      '4': 'Заявки с исправленными ошибками',
      '5': 'Заявки с частично исправленными ошибками',
    },
    [REQUEST_STATUSES_TRANSLATION_KEY]: {
      ...REQUEST_STATUS_ID_TO_NAME,
    },
    [ColumnFiltersTranslations.NotAssigned]: 'Не назначен',
  };

  private additionalFiltersButtons = new BehaviorSubject<ActiveFilterChip[]>(
    [],
  );

  // toggle for mobile requests list compact mode
  public $mobileRequestsListCompactMode = signal(
    !!this.localStorageService.getItem(MOBILE_REQUESTS_LIST_COMPACT_MODE),
  );

  constructor() {
    this.outkeepTranslateService
      .translate([
        REQUEST_QUALITY_STATUSES_TRANSLATION_KEY,
        REQUEST_STATUSES_TRANSLATION_KEY,
        ColumnFiltersTranslations.NotAssigned,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translations => {
        this.translations = translations;
      });
  }

  public get filtersData$() {
    return this.filtersData.asObservable();
  }

  public get additionalFiltersButtons$(): Observable<ActiveFilterChip[]> {
    return this.additionalFiltersButtons.asObservable();
  }

  public initFiltersData() {
    const logError = (message: string, err: HttpErrorResponse) => {
      // Запросы анонима отклоняются AuthInterceptor до сети, внешний auth уже открыт.
      if (!isAnonymousAuthRejection(err)) {
        console.error(message, err);
      }
    };
    const handleError = (message: string) => (err: HttpErrorResponse) => {
      logError(message, err);

      return of([]);
    };
    const installers$: Observable<InstallersLoadResult> =
      this.requestsApiService.getInstallers$().pipe(
        map<InstallerPerson[], InstallersLoadResult>(installers => ({
          installers,
          installersLoadFailed: false,
        })),
        catchError((err: HttpErrorResponse) => {
          logError('Error fetching installers:', err);

          return of<InstallersLoadResult>({
            installers: [],
            installersLoadFailed: true,
          });
        }),
      );

    forkJoin({
      installers: installers$, // СЛС
      installersGroups: this.requestsApiService
        .getInstallersGroups()
        .pipe(catchError(handleError('Error fetching installers groups:'))), // группы СЛС
      statuses: this.requestsApiService
        .getRequestStatuses$()
        .pipe(catchError(handleError('Error fetching request statuses:'))), // Статус заявки
      counterparties: this.requestsApiService
        .getAllRequestsCounterparties$()
        .pipe(catchError(handleError('Error fetching counterparties:'))), // Контрагенты
      requestTypes: this.requestsApiService
        .getAvailableRequestTypes$()
        .pipe(catchError(handleError('Error fetching request types:'))),
      products: this.requestsApiService
        .getAvailableProducts$({ scopeToVisibleRequests: true })
        .pipe(catchError(handleError('Error fetching products:'))),
    })
      .pipe(
        shareReplay(1),
        map(({ installers, ...project }): LoadedRequestFiltersData => {
          return {
            ...project,
            installers: installers.installers,
            installersLoadFailed: installers.installersLoadFailed,
            statuses: project.statuses.sort(
              (a: RequestStatus, b: RequestStatus) =>
                (a?.sort || 999) - (b?.sort || 999),
            ),
          };
        }),
        tap(this.parseQueryParamsForActiveFilters.bind(this)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(project => this.filtersData.next(project));
  }

  public parseQueryParamsForActiveFilters(
    project: LoadedRequestFiltersData,
  ): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const additionalFiltersButtons: ActiveFilterChip[] = [];

        if ('rqQuery' in params) {
          const rqQuery = RequestFiltersService.parseRqQuery(params['rqQuery']);

          if (rqQuery) {
            Object.entries(rqQuery).forEach(([key, value]: [string, any]) => {
              if (TECHNICAL_FILTER_KEYS.includes(key as keyof FilterQuery)) {
                return;
              }

              switch (key) {
                case 'statusId':
                  if (
                    this.localStorageService.getItem(HIDE_COMPLETED_FILTER) ||
                    this.localStorageService.getItem(SHOW_IN_WORK_FILTER)
                  ) {
                    break;
                  }
                  additionalFiltersButtons.push({
                    title: 'statusId',
                    value:
                      this.translations.request_statuses[
                        value.toString() as keyof typeof this.translations.request_statuses
                      ],
                    removable: true,
                  });
                  break;
                case 'installerId': {
                  const installerGroupsToAdd: InstallersGroup[] = [];

                  if (project.installersGroups.length) {
                    // if all installers from project.installers are in groups, we can show group names
                    const selectedInstallerIds = value;

                    project.installersGroups.forEach(group => {
                      const groupInstallersIds = group.installers.map(
                        installer => installer.id,
                      );

                      if (
                        groupInstallersIds.every(id =>
                          selectedInstallerIds.includes(id),
                        )
                      ) {
                        installerGroupsToAdd.push(group);
                      }
                    });
                  }
                  if (installerGroupsToAdd.length) {
                    additionalFiltersButtons.push({
                      title: 'installerGroup',
                      value: installerGroupsToAdd
                        .map(group => group.name)
                        .join(', '),
                      removable: true,
                    });
                  }

                  additionalFiltersButtons.push({
                    title: 'installerId',
                    value: [
                      {
                        id: null,
                        fullName:
                          this.translations[
                            ColumnFiltersTranslations.NotAssigned
                          ],
                        lastName:
                          this.translations[
                            ColumnFiltersTranslations.NotAssigned
                          ],
                      },
                      ...project.installers,
                    ]
                      .filter(installer => value.includes(installer.id))
                      .map(installer => installer.lastName)
                      .join(', '),
                    removable: true,
                  });
                  break;
                }
                case 'productsIds': {
                  const selectedIds = Array.isArray(value)
                    ? toFiniteNumbers(value)
                    : [];

                  additionalFiltersButtons.push({
                    title: 'productsIds',
                    value: project.products
                      .filter(product => selectedIds.includes(product.id))
                      .map(product => product.name)
                      .join(', '),
                    removable: true,
                  });
                  break;
                }
                case 'counterpartyOwnerId':
                  additionalFiltersButtons.push({
                    title: 'counterpartyOwnerId',
                    value: project.counterparties.find(
                      (counterparty: Counterparty) => counterparty.id === value,
                    )?.name,
                    removable: true,
                  });
                  break;
                case 'qualityControlStatus':
                  additionalFiltersButtons.push({
                    title: 'qualityControlStatus',
                    value:
                      this.translations.quality_statuses[
                        value.toString() as keyof typeof this.translations.quality_statuses
                      ],
                    removable: true,
                  });
                  break;

                case 'releaseDatePeriod':
                  if (
                    this.localStorageService.getItem(SHOW_TODAY_FILTER) ||
                    this.localStorageService.getItem(SHOW_TOMORROW_FILTER)
                  ) {
                    break;
                  }
                  additionalFiltersButtons.push({
                    title: 'releaseDatePeriod',
                    value: `${RequestFiltersService.formatDateDMY(value.startDate)} - ${RequestFiltersService.formatDateDMY(value.endDate)}`,
                    removable: true,
                  });
                  break;

                case 'releaseDate':
                  if (
                    this.localStorageService.getItem(SHOW_TODAY_FILTER) ||
                    this.localStorageService.getItem(SHOW_TOMORROW_FILTER)
                  ) {
                    break;
                  }
                  additionalFiltersButtons.push({
                    title: 'releaseDate',
                    value: RequestFiltersService.formatDateDMY(value),
                    removable: true,
                  });
                  break;
                case 'requestType':
                  additionalFiltersButtons.push({
                    title: 'requestType',
                    value: this.formatRequestTypeFilter(value),
                    removable: true,
                    pinnedToEnd: true,
                  });
                  break;
                // фильтры кнопок «Исполнитель»/«Заказчик» не показываем
                // чипсами — их состояние видно на самих кнопках
                case 'contractorCounterpartyId':
                case 'contractorCounterpartySubdivisionId':
                  break;
                default:
                  additionalFiltersButtons.push({
                    title: key as keyof FilterQuery,
                    value: value,
                    removable: true,
                  });
              }
            });
          }
        }
        this.additionalFiltersButtons.next(additionalFiltersButtons);
      });
  }

  private static formatDateDMY(input: Date | string): string {
    const d = new Date(input);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  }

  private formatRequestTypeFilter(value: unknown): string {
    const values = Array.isArray(value) ? value : [value];

    return values
      .map(item =>
        typeof item === 'string'
          ? (LOGISTIC_REQUEST_TYPE_LABELS[item as LogisticRequestType] ?? item)
          : String(item),
      )
      .join(', ');
  }

  private static parseRqQuery(value: unknown): Record<string, unknown> {
    if (typeof value !== 'string') return {};

    try {
      const parsed: unknown = JSON.parse(value);

      return parsed !== null &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed)
        ? { ...parsed }
        : {};
    } catch {
      return {};
    }
  }
}
